"use client";

import { Button, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import type { CreateServiceTicketPayload } from "@/_services/postRequestForm";
import { InputField } from "@/_components/ui/form/InputField";
import { SimpleSelectField } from "@/_components/ui/form/SimpleSelect";
import { TextareaField } from "@/_components/ui/form/TextareaField";
import { FileUploadV2 } from "@/_components/ui/FileUploadV2";
import { useAuthStore } from "@/_stores/useAuthStore";
import { useTranslations } from "next-intl";
import { IoSend } from "react-icons/io5";

type SubmitState = "idle" | "success" | "error";

type SupportFormValues = {
  ticketTitle: string;
  email: string;
  phoneNumber?: string | null;
  requestType: string[];
  effortLevel: string[];
  priority: string[];
  detail?: string | null;
};

const schema = yup.object({
  ticketTitle: yup.string().required("Ticket title is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phoneNumber: yup.string().optional().nullable(),
  requestType: yup.array(yup.string().required()).min(1).required(),
  effortLevel: yup.array(yup.string().required()).min(1).required(),
  priority: yup.array(yup.string().required()).min(1).required(),
  detail: yup.string().optional().nullable(),
});

export function SupportRequestForm() {
  const userEmail = useAuthStore((state) => state.user?.userInfo?.email || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const t = useTranslations("settings.request_service");
  const methods = useForm<SupportFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      ticketTitle: "",
      email: userEmail,
      phoneNumber: "",
      requestType: ["Service Request"],
      effortLevel: ["Medium"],
      priority: ["Medium"],
      detail: "",
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    if (!userEmail) return;
    methods.setValue("email", userEmail, { shouldValidate: true });
  }, [methods, userEmail]);

  const resetFormForNewSubmit = () => {
    setSubmitState("idle");
    setApiError(null);
    setIsSubmitting(false);
    setAttachedFiles([]);
    setFileUploadKey((k) => k + 1);

    methods.reset({
      ticketTitle: "",
      email: userEmail,
      phoneNumber: "",
      requestType: ["Service Request"],
      effortLevel: ["Medium"],
      priority: ["Medium"],
      detail: "",
    });
  };

  const onSubmit = methods.handleSubmit(async (values) => {
    setIsSubmitting(true);
    setApiError(null);
    setSubmitState("idle");

    try {
      const payload: CreateServiceTicketPayload = {
        ticketTitle: values.ticketTitle,
        email: values.email,
        phoneNumber: values.phoneNumber ? values.phoneNumber : undefined,
        requestType: values.requestType?.[0],
        effortLevel: values.effortLevel?.[0],
        priority: values.priority?.[0],
        detail: values.detail ? values.detail : undefined,
      };

      const formData = new FormData();
      formData.append("ticketTitle", payload.ticketTitle);
      formData.append("email", payload.email);
      if (payload.phoneNumber)
        formData.append("phoneNumber", payload.phoneNumber);
      if (payload.requestType)
        formData.append("requestType", payload.requestType);
      if (payload.effortLevel)
        formData.append("effortLevel", payload.effortLevel);
      if (payload.priority) formData.append("priority", payload.priority);
      if (payload.detail) formData.append("detail", payload.detail);
      attachedFiles.forEach((file) => {
        formData.append("attachFile", file, file.name);
      });

      const res = await fetch("/api/notion/post-request-form/support", {
        method: "POST",
        body: formData,
      });

      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Failed to submit request");
      }

      setSubmitState("success");
    } catch (e) {
      setApiError(e instanceof Error ? e.message : String(e));
      setSubmitState("error");
    } finally {
      setIsSubmitting(false);
    }
  });

  if (submitState === "success") {
    return (
      <VStack align="stretch" gap={4} pt={2}>
        <Text fontWeight="semibold">{t("sussess_message")}</Text>
        <HStack justify="flex-end" gap={2}>
          <Button onClick={resetFormForNewSubmit} variant="outline">
            {t("submit_another")}
          </Button>
        </HStack>
      </VStack>
    );
  }

  if (submitState === "error") {
    return (
      <VStack align="stretch" gap={4} pt={2}>
        <Text fontWeight="semibold" color="red.500">
          Submit failed
        </Text>
        {apiError ? <Text color="red.500">{apiError}</Text> : null}
        <HStack justify="flex-end">
          <Button onClick={resetFormForNewSubmit} variant="outline">
            {t("submit_again")}
          </Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <FormProvider {...methods}>
      <VStack as="form" align="stretch" gap={4} onSubmit={onSubmit}>
        <Text>{t("message")}</Text>

        <InputField
          name="ticketTitle"
          label={t("ticket_title")}
          isFloating={true}
          isRequired={true}
        />

        <InputField
          name="email"
          label={t("email")}
          isFloating={true}
          isRequired={true}
          type="email"
          isDisabled={true}
        />

        <InputField name="phoneNumber" label={t("phone")} isFloating={true} />

        <SimpleSelectField
          name="requestType"
          label={t("request_type")}
          listItems={[
            { label: "Issues Report", value: "Issues Report" },
            { label: "Service Request", value: "Service Request" },
            { label: "Issue Report", value: "Issue Report" },
          ]}
          isFloating={true}
          isRequired={true}
        />

        <SimpleSelectField
          name="priority"
          label={t("priority")}
          listItems={[
            { label: "High", value: "High" },
            { label: "Medium", value: "Medium" },
            { label: "Low", value: "Low" },
          ]}
          isFloating={true}
          isRequired={true}
        />

        <TextareaField
          name="detail"
          label={t("detail")}
          isFloating={true}
          rows={6}
        />

        <VStack align="stretch" gap={1}>
          <FileUploadV2
            key={fileUploadKey}
            maxFile={5}
            onFileAccept={(files) => {
              setAttachedFiles(files.files);
            }}
            fileAccepts={[
              { mimeType: "application/pdf", displayName: "pdf" },
              {
                mimeType:
                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                displayName: "docx",
              },
              { mimeType: "application/msword", displayName: "doc" },
              { mimeType: "image/png", displayName: "png" },
              { mimeType: "image/jpeg", displayName: "jpg" },
            ]}
            title={<Text as="span">{t("attach_file")}</Text>}
          />
          {attachedFiles.length > 0 ? (
            <Text fontSize="sm" color="gray.500">
              Selected: {attachedFiles.map((f) => f.name).join(", ")}
            </Text>
          ) : null}
        </VStack>

        {apiError && <Text color="red.500">{apiError}</Text>}

        <Button size="lg" type="submit" loading={isSubmitting}>
          <HStack gap={2}>
            {t("submit")}
            <Icon as={IoSend} boxSize={4} />
          </HStack>
        </Button>
      </VStack>
    </FormProvider>
  );
}
